import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { PlainTime, TimeZone } from 'temporal-luxon';
import { TimeShort } from '@medable/i18n-core';
import { LocaleContext, isTime12Hour, useLocalizeMessage, Message } from '../../../i18n/src';
import { Icon } from '../../../icon/src';
import { TimePickerInput } from './time_picker_input';
import { areAllOptionsSelected, areOptionsFilled, getDayPeriodOptions, getHours12Options, getHours24Options, getMinuteOptions, timezoneOptions, } from './time_picker_utils';
const TIME_PICKER_WIDTHS = {
    base: '',
    full: 'nucleus-w-full',
};
export const TimePicker = ({ hasError, errorMessage, showTimeZone = false, onChange, value, onError, onPartialChange, required = false, disabled, minutesOptions, width = 'base', }) => {
    // eslint-disable-next-line dot-notation
    if (showTimeZone && process.env['NODE_ENV'] === 'development') {
        console.error("TimePicker: showTimeZone isn't fully supported. See https://jira.devops.medable.com/browse/MWF-622");
    }
    const [allFieldsRequiredError, setAllFieldsRequiredError] = useState(false);
    const [internalOptions, setInternalOptions] = useState({
        hour: { option: null, isTouched: false },
        minute: { option: null, isTouched: false },
        dayPeriod: { option: null, isTouched: false },
        timeZoneName: { option: null, isTouched: false },
    });
    const locale = useContext(LocaleContext);
    const time12Hour = isTime12Hour(useContext(LocaleContext));
    const localizeMessage = useLocalizeMessage();
    const timeShortFormatter = useMemo(() => new TimeShort(locale, { timeZoneName: 'short' }), [locale]);
    const hourOptions = useMemo(() => (time12Hour ? getHours12Options({ timeShortFormatter }) : getHours24Options({ timeShortFormatter })), [time12Hour, timeShortFormatter]);
    const minuteOptions = useMemo(() => getMinuteOptions({ timeShortFormatter, minutesOptions }), [timeShortFormatter, minutesOptions]);
    const amPmOptions = useMemo(() => getDayPeriodOptions({ timeShortFormatter }), [timeShortFormatter]);
    useEffect(() => {
        setInternalOptions((prevInternalOptions) => {
            var _a, _b, _c, _d;
            if (value.time) {
                const newHour = time12Hour ? value.time.hour % 12 : value.time.hour;
                const newDayPeriod = value.time.hour >= 12 ? amPmOptions[1].value : amPmOptions[0].value;
                const nextInternalState = {
                    hour: {
                        option: (_a = hourOptions.find((hour) => hour.value === newHour)) !== null && _a !== void 0 ? _a : null,
                        isTouched: true,
                    },
                    minute: {
                        option: (_b = minuteOptions.find((minute) => { var _a; return minute.value === ((_a = value.time) === null || _a === void 0 ? void 0 : _a.minute); })) !== null && _b !== void 0 ? _b : null,
                        isTouched: true,
                    },
                    dayPeriod: {
                        option: time12Hour ? ((_c = amPmOptions.find((dayPeriod) => dayPeriod.value === newDayPeriod)) !== null && _c !== void 0 ? _c : null) : null,
                        isTouched: !!time12Hour,
                    },
                    timeZoneName: {
                        option: value.timeZone
                            ? ((_d = timezoneOptions.find((timezone) => { var _a; return timezone.value === ((_a = value.timeZone) === null || _a === void 0 ? void 0 : _a.toString()); })) !== null && _d !== void 0 ? _d : null)
                            : null,
                        isTouched: showTimeZone,
                    },
                };
                if (allFieldsRequiredError && areAllOptionsSelected(nextInternalState, !!time12Hour, showTimeZone)) {
                    setAllFieldsRequiredError(false);
                    onError === null || onError === void 0 ? void 0 : onError(false);
                }
                return nextInternalState;
            }
            return prevInternalOptions;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time12Hour, showTimeZone, value]);
    const handleChange = (chosenOption, type) => {
        var _a, _b, _c, _d, _e, _f;
        const nextInternalOptions = Object.assign(Object.assign({}, internalOptions), { [type]: { option: chosenOption, isTouched: true } });
        const everyOptionHasValue = areAllOptionsSelected(nextInternalOptions, time12Hour, showTimeZone);
        const atLeastOneFieldFilled = areOptionsFilled(nextInternalOptions, showTimeZone, true);
        const atLeastOneFieldEmpty = areOptionsFilled(nextInternalOptions, showTimeZone, false);
        if (everyOptionHasValue) {
            const hour = (_b = (_a = nextInternalOptions.hour) === null || _a === void 0 ? void 0 : _a.option) === null || _b === void 0 ? void 0 : _b.value;
            const internalOptionsFormatted = {
                hour: !nextInternalOptions.dayPeriod.option || nextInternalOptions.dayPeriod.option.value === 'AM'
                    ? hour
                    : hour + 12,
                minute: (_d = (_c = nextInternalOptions.minute) === null || _c === void 0 ? void 0 : _c.option) === null || _d === void 0 ? void 0 : _d.value,
                timeZone: (_f = (_e = nextInternalOptions.timeZoneName) === null || _e === void 0 ? void 0 : _e.option) === null || _f === void 0 ? void 0 : _f.value,
            };
            const newValue = {
                time: PlainTime.from({
                    hour: internalOptionsFormatted.hour,
                    minute: internalOptionsFormatted.minute,
                }),
                timeZone: internalOptionsFormatted.timeZone ? new TimeZone(internalOptionsFormatted.timeZone) : null,
            };
            if (allFieldsRequiredError) {
                setAllFieldsRequiredError(false);
                onError === null || onError === void 0 ? void 0 : onError(false);
            }
            onChange(newValue);
        }
        else if (!allFieldsRequiredError &&
            atLeastOneFieldFilled &&
            Object.values(nextInternalOptions).some((internalOption) => internalOption.isTouched && !internalOption.option)) {
            // if a field has been touched and its value is null, we need to set the error state for all fields being required
            onChange({ time: null, timeZone: null });
            setAllFieldsRequiredError(true);
            onError === null || onError === void 0 ? void 0 : onError(true);
        }
        onPartialChange === null || onPartialChange === void 0 ? void 0 : onPartialChange(atLeastOneFieldFilled && atLeastOneFieldEmpty);
        setInternalOptions(nextInternalOptions);
    };
    const showError = allFieldsRequiredError || hasError;
    const parts = timeShortFormatter.formatToParts(new PlainTime(0, 0));
    const inputLayout = parts
        .filter((part) => {
        if (['hour', 'minute', 'dayPeriod'].includes(part.type)) {
            return true;
        }
        if (part.type === 'timeZoneName' && showTimeZone) {
            return true;
        }
        return false;
    })
        .map(({ type }) => {
        switch (type) {
            case 'hour':
                return (_jsx(TimePickerInput, { type: type, label: localizeMessage(new Message({ id: 'hour-label', source: 'Hour' })), value: internalOptions.hour.option, onChange: handleChange, options: hourOptions, hasError: !!showError, required: required, disabled: disabled, width: width }, type));
            case 'minute':
                return (_jsx(TimePickerInput, { type: type, label: localizeMessage(new Message({ id: 'minute-label', source: 'Minute' })), value: internalOptions.minute.option, onChange: handleChange, options: minuteOptions, hasError: !!showError, required: required, disabled: disabled, width: width }, type));
            case 'dayPeriod':
                return (_jsx(TimePickerInput, { type: type, label: localizeMessage(new Message({ id: 'day-period-label', source: 'AM/PM' })), value: internalOptions.dayPeriod.option, onChange: handleChange, options: amPmOptions, hasError: !!showError, required: required, disabled: disabled, width: width }, type));
            case 'timeZoneName':
                return showTimeZone ? (_jsx("div", { className: 'nucleus-flex-1', children: _jsx(TimePickerInput, { type: type, label: localizeMessage(new Message({ id: 'time-zone-label', source: 'Timezone' })), value: internalOptions.timeZoneName.option, onChange: handleChange, options: timezoneOptions, hasError: !!showError, required: required, disabled: disabled, width: width }) }, type)) : undefined;
            default:
                return undefined;
        }
    });
    return (_jsxs("div", { className: `nucleus-box-border nucleus-inline-flex nucleus-flex-col nucleus-font-sans ${TIME_PICKER_WIDTHS[width]}`, children: [_jsx("div", { className: 'nucleus-flex nucleus-flex-wrap nucleus-gap-4', children: inputLayout }), showError && (_jsxs("div", { className: 'nucleus-box-border nucleus-flex nucleus-items-center nucleus-gap-2 nucleus-text-text-error nucleus-mt-2.5', children: [_jsx(Icon, { name: 'error', color: 'red' }), allFieldsRequiredError ? localizeMessage('All fields are required when one is selected') : errorMessage] }))] }));
};
//# sourceMappingURL=time_picker.js.map